import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import { Trans } from 'react-i18next';
import { perSessionBannersKey } from 'constants/localStorageKeys';
import { CircleInfo } from '@sighten/beacon/icons';
import { type IconWrapperComponentType } from '@sighten/beacon';
import { BannerButton, BannerContent, BannerStyled, BannerText, LinkStyled } from './Banner.styles';
import { initializeBannerVisibility } from './utils/initializeBannerVisibility';
import { CloseIcon } from '../icons/CloseIcon';
// import { CircleInfoIcon } from '../icons/CircleInfoIcon';
import { Button } from '../Button/Button';

export interface BannerProps {
  bannerText: string;
  bgColor?: string;
  link?: { text?: string; url?: string; onClick?: () => void };
  bannerKey?: string;
  Icon?: IconWrapperComponentType;
  dismissable?: boolean;
  fitContent?: boolean;
  onVisibilityChange?: (isVisible: boolean) => void;
}

/**
 * @param {string} [bannerText] - Main body text of banner
 * @param {Color} [bgColor] - Background color of banner
 * @param {{text: string; url: string; onClick: () => void}} [link] - Clickable link (optional)
 * @param {string} [bannerKey] - Optional banner key used for handling hiding/showing on a per session basis. Should be unique across all <Banner /> components used in app. Omit to handle hiding/showing with just react state. (optional)
 * @param {JSX.Element} [icon] - Icon preceding text content
 * @param {boolean} [dismissable] - Can the banner be dismissed (optional)
 * @param {boolean} [fitContent] - Whether the banner should fit its content width (optional)
 * @param {(isVisible: boolean) => void} [onVisibilityChange] - Callback fired when banner visibility changes (optional)
 */
export function Banner({
  bannerText,
  link,
  bannerKey,
  bgColor,
  Icon = CircleInfo,
  dismissable = true,
  fitContent = false,
  onVisibilityChange,
}: BannerProps) {
  const [bannerVisible, setBannerVisible] = useState(true);
  const { colors } = useTheme();

  useEffect(() => {
    const visibility = initializeBannerVisibility(bannerKey);
    setBannerVisible(visibility);
    onVisibilityChange?.(visibility);
  }, [bannerKey, onVisibilityChange]);

  const handleClose = () => {
    const perSessionBannersRaw = localStorage.getItem(perSessionBannersKey);
    if (perSessionBannersRaw != null && bannerKey) {
      const bannersObj = JSON.parse(perSessionBannersRaw);
      bannersObj[bannerKey] = false;
      localStorage.setItem(perSessionBannersKey, JSON.stringify(bannersObj));
    }
    setBannerVisible(false);
    onVisibilityChange?.(false);
  };

  let action: React.ReactElement | null = null;

  if (link?.text) {
    if (link.url) {
      action = (
        <LinkStyled href={link.url} target="_blank" rel="noreferrer">
          {link.text}
        </LinkStyled>
      );
    }

    if (link.onClick) {
      action = <Button styleVariant="tertiary" label={link.text} onClick={link.onClick} />;
    }
  }

  return bannerVisible ? (
    <BannerStyled $bgColor={bgColor} $fitContent={fitContent}>
      <Icon size="md" />
      <BannerContent>
        <BannerText as="p">
          <Trans>
            {bannerText} {action}
          </Trans>
        </BannerText>
      </BannerContent>
      {dismissable && (
        <BannerButton onClick={handleClose}>
          <CloseIcon color={colors.iconIconPrimarySecondary1100} />
        </BannerButton>
      )}
    </BannerStyled>
  ) : null;
}

export default Banner;
